/**
 * OturumDisiSayfa
 *
 * Giriş, otel seçimi ve hata sayfalarının ortak iskeleti: solda marka paneli,
 * sağda içerik. Marka adı ve logosu `api/public/marka` ucundan, isteğin alan
 * adına bakılarak gelir (giriş öncesi hangi otelde olunduğu bilinmez; bir
 * hesabın birden fazla oteli olabilir).
 *
 * lg altında marka paneli kompakt bir üst banda iner.
 *
 * Props:
 *   altYazi   – sol paneldeki açıklama metni
 *   baslik    – sol paneldeki büyük başlık (varsayılan: "Otelinizi tek ekrandan yönetin.")
 *   dar       – true ise sağ içerik dar kolonda ortalanır (hata sayfaları)
 *   children  – sağ içerik
 */

import useMarka from '../hooks/useMarka';

const OZELLIKLER = [
  { ikon: '🛎️', baslik: 'Resepsiyon', metin: 'Rezervasyon, check-in ve oda planı tek ekranda' },
  { ikon: '💳', baslik: 'Kasa ve cari', metin: 'Tahsilat, taksit ve günlük kasa raporları' },
  { ikon: '📊', baslik: 'Raporlar', metin: 'Doluluk, ciro, KDV ve konaklama vergisi' },
];

function Logo({ marka, boyut = 'buyuk' }) {
  const kutu = boyut === 'buyuk'
    ? 'w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25'
    : 'w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 shadow-lg shadow-violet-200';
  const gorsel = boyut === 'buyuk' ? 'w-9 h-9' : 'w-8 h-8';

  return (
    <div className={`${kutu} flex items-center justify-center overflow-hidden shadow-inner flex-shrink-0`}>
      <img src={marka.logoUrl || '/Hotel.png'} alt="" className={`${gorsel} object-contain`} />
    </div>
  );
}

export default function OturumDisiSayfa({
  altYazi,
  baslik = 'Otelinizi\ntek ekrandan\nyönetin.',
  dar = false,
  children,
}) {
  const marka = useMarka();

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sol: marka paneli */}
      <div className="relative hidden lg:flex lg:w-[46%] xl:w-[42%] flex-col justify-between overflow-hidden
                      bg-gradient-to-br from-violet-700 via-indigo-800 to-slate-900 text-white p-12">
        <div className="absolute -top-24 -left-16 w-96 h-96 bg-violet-400/25 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-20 w-[28rem] h-[28rem] bg-fuchsia-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 w-72 h-72 bg-cyan-300/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative">
          <div className="flex items-center gap-4">
            <Logo marka={marka} />
            <div className="min-w-0">
              <p className="font-black text-lg tracking-tight leading-none truncate">{marka.ad}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 mt-1.5">
                Otel Yönetim Sistemi
              </p>
            </div>
          </div>

          <h2 className="mt-16 text-4xl xl:text-5xl font-black leading-[1.1] tracking-tight whitespace-pre-line">
            {baslik}
          </h2>
          {altYazi && <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-sm">{altYazi}</p>}
        </div>

        <div className="relative space-y-4">
          {OZELLIKLER.map(o => (
            <div key={o.baslik} className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-lg flex-shrink-0 font-emoji">
                {o.ikon}
              </span>
              <div>
                <p className="font-bold text-sm leading-none">{o.baslik}</p>
                <p className="text-white/60 text-xs mt-1.5 leading-relaxed">{o.metin}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="relative text-[10px] font-bold uppercase tracking-[0.2em] text-white/35 truncate">
          © {new Date().getFullYear()} {marka.ad}
        </p>
      </div>

      {/* Sağ: içerik */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className={`w-full ${dar ? 'max-w-sm text-center' : 'max-w-md'}`}>
          {/* lg altında görünen kompakt marka bandı */}
          <div className={`lg:hidden flex items-center gap-3 mb-10 ${dar ? 'justify-center' : ''}`}>
            <Logo marka={marka} boyut="kucuk" />
            <div className="min-w-0 text-left">
              <p className="font-black text-gray-800 tracking-tight leading-none truncate">{marka.ad}</p>
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mt-1.5">
                Otel Yönetim Sistemi
              </p>
            </div>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
